import { useEffect, useState, useMemo } from "react";
import { Outlet, NavLink, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import InstallPrompt from "@/components/InstallPrompt";
import ChangePasswordModal from "@/components/ChangePasswordModal";
import {
  LayoutDashboard, Package, ClipboardList, LogOut, Building2,
  Eye, X, Menu,
} from "lucide-react";

const PORTAL_NAV = [
  { to: "/portal", end: true, label: "Overview", icon: LayoutDashboard, testid: "portal-sidebar-overview" },
  { to: "/portal/inventory", label: "Inventory & Pricing", icon: Package, testid: "portal-sidebar-inventory" },
  { to: "/portal/orders", label: "My Orders", icon: ClipboardList, testid: "portal-sidebar-orders" },
];

export default function PortalLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const asCompany = searchParams.get("as_company");
  const isImpersonating = Boolean(asCompany) && (user?.role === "admin" || user?.role === "user");
  const [companyName, setCompanyName] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (!isImpersonating) { setCompanyName(""); return; }
    api.get(`/companies/${asCompany}`).then(({ data }) => setCompanyName(data?.Company_Name || "")).catch(() => {});
  }, [isImpersonating, asCompany]);

  // Append ?as_company= to nav links while impersonating
  const navItems = useMemo(() => {
    if (!isImpersonating) return PORTAL_NAV;
    return PORTAL_NAV.map((it) => ({ ...it, to: `${it.to}?as_company=${asCompany}` }));
  }, [isImpersonating, asCompany]);

  // If non-admin staff somehow lands on /portal without the param, send them home.
  if (!user) return null;
  if ((user.role === "admin" || user.role === "user") && !isImpersonating) {
    return (
      <div className="min-h-screen bg-zinc-100 flex items-center justify-center p-10">
        <div className="es-card p-8 max-w-md text-center">
          <h2 className="font-display text-2xl font-bold text-zinc-900 mb-2">Pick a company to view</h2>
          <p className="text-sm text-zinc-600 mb-5">
            The reseller portal is scoped per partner. Go to <strong>Companies</strong> and click the
            <span className="inline-flex items-center gap-1 mx-1"><Eye className="w-3.5 h-3.5" /> View portal</span>
            icon on any portal-enabled row.
          </p>
          <button onClick={() => navigate("/companies", { replace: true })} className="es-btn-primary">
            Go to Companies
          </button>
        </div>
      </div>
    );
  }

  const handleLogout = async () => { await logout(); navigate("/login", { replace: true }); };
  const exitImpersonation = () => navigate("/companies", { replace: true });

  return (
    <div className="min-h-screen flex bg-zinc-100">
      {mobileOpen && (
        <div onClick={() => setMobileOpen(false)} className="fixed inset-0 bg-black/40 z-40 lg:hidden" aria-hidden="true" />
      )}
      <aside
        className={[
          "bg-zinc-900 text-zinc-300 flex flex-col z-50",
          "fixed inset-y-0 left-0 w-72 transform transition-transform duration-200 ease-out",
          mobileOpen ? "translate-x-0" : "-translate-x-full",
          "lg:static lg:translate-x-0 lg:w-64",
        ].join(" ")}
      >
        <div className="px-6 py-5 border-b border-zinc-800 flex items-center gap-3">
          <img src="/edenstone-logo.png" alt="Edenstone Masonry" className="w-14 h-14 object-contain bg-white rounded-md p-1" />
          <div className="flex-1 min-w-0">
            <div className="font-display font-extrabold text-white text-base leading-none tracking-tight">EDENSTONE</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mt-1">Reseller Portal</div>
          </div>
          <button
            data-testid="sidebar-close-btn"
            onClick={() => setMobileOpen(false)}
            className="lg:hidden text-zinc-400 hover:text-white p-1"
            aria-label="Close menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <nav className="flex-1 py-4">
          <div className="px-6 mb-3 es-overline text-zinc-500">Partner Tools</div>
          {navItems.map((it) => {
            const Icon = it.icon;
            return (
              <NavLink
                key={it.testid}
                to={it.to}
                end={it.end}
                data-testid={it.testid}
                className={({ isActive }) =>
                  [
                    "flex items-center gap-3 px-6 py-2.5 text-sm font-medium border-l-4 transition-colors",
                    isActive ? "bg-zinc-800/60 text-white border-brand-500" : "border-transparent hover:text-white hover:bg-zinc-800/40",
                  ].join(" ")
                }
              >
                <Icon className="w-4 h-4" /> {it.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="border-t border-zinc-800 px-4 py-4">
          <div className="flex items-center gap-3 mb-3 px-2">
            <div className="w-9 h-9 bg-zinc-700 flex items-center justify-center text-white font-semibold text-sm rounded-sm">
              {user.full_name?.[0] || user.email[0].toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-white truncate">{user.full_name || user.email}</div>
              <span className="es-badge es-badge-neutral">{user.role}</span>
            </div>
          </div>
          {isImpersonating ? (
            <button
              data-testid="portal-exit-impersonation"
              onClick={exitImpersonation}
              className="w-full flex items-center justify-center gap-2 h-9 text-sm font-semibold text-amber-200 hover:text-white hover:bg-zinc-800 rounded-sm transition-colors"
            >
              <X className="w-4 h-4" /> Exit impersonation
            </button>
          ) : (
            <button
              data-testid="portal-logout-button"
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 h-9 text-sm font-semibold text-zinc-300 hover:text-white hover:bg-zinc-800 rounded-sm transition-colors"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          )}
          {!isImpersonating && <InstallPrompt />}
        </div>
      </aside>
      <main className="flex-1 min-w-0 overflow-x-hidden">
        {isImpersonating && (
          <div
            data-testid="portal-impersonation-banner"
            className="bg-amber-100 border-b border-amber-300 text-amber-900 px-6 py-2.5 text-sm flex items-center justify-between gap-4"
          >
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4" />
              <span>
                <strong>Admin view</strong> — you are viewing the portal as <strong>{companyName || "this company"}</strong>.
              </span>
            </div>
            <button
              onClick={exitImpersonation}
              data-testid="portal-impersonation-exit-banner"
              className="text-amber-900 hover:text-amber-950 font-semibold underline underline-offset-2 text-xs"
            >
              Exit
            </button>
          </div>
        )}
        <header className="h-14 bg-white border-b border-zinc-200 flex items-center justify-between px-4 lg:px-8 gap-3">
          <button
            data-testid="sidebar-open-btn"
            onClick={() => setMobileOpen(true)}
            className="lg:hidden text-zinc-700 hover:text-zinc-900 p-1 flex-shrink-0"
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2 text-sm text-zinc-500 min-w-0">
            <Building2 className="w-4 h-4 text-brand-600 flex-shrink-0" />
            <span className="font-mono text-xs hidden sm:inline">PARTNER SESSION</span>
            <span className="text-zinc-300 hidden sm:inline">·</span>
            <span className="font-mono text-xs truncate">{user.email}</span>
          </div>
          <div className="hidden md:block text-[11px] font-mono uppercase tracking-wider text-zinc-400 flex-shrink-0">Edenstone Reseller Portal · v1.0</div>
        </header>
        <div className="p-4 sm:p-6 lg:p-8 animate-fade-in"><Outlet /></div>
      </main>
      {user.must_change_password && <ChangePasswordModal />}
    </div>
  );
}
